import { css } from '@emotion/react';
import { type IconProp } from '@fortawesome/fontawesome-svg-core';
import {
  faPaperPlane, faPhone, faWindow,
} from '@fortawesome/pro-solid-svg-icons';
import {
  forwardRef, type MouseEvent, useCallback, useMemo, useState,
} from 'react';
import { FontAwesomeIcon } from '~/_shared/baseComponents/icon/fontAwesomeIcon.component';
import { guaranteeHash } from '~/_shared/components/colorPicker/colorPicker.helpers';
import { LimitNumberOfLinesComponent } from '~/_shared/components/limitNumberOfLines/limitNumberOfLines.component';
import { ColumnRole } from '~/_shared/types/columnRole.enum';
import {
  getIsPhoneNumber, validateEmail,
} from '~/_shared/utils/throttle/validation/validation.helpers';
import { getItemStyles } from '~/customizeLocationPanel/customizeLocationPanel.constants';
import { type BubbleEditorItem } from '../locationData.types';
import { type ColumnStyleSettingsData } from '../panelSettings.types';

type FontProps = {
  fontColor: string;
  fontSize: string;
};

const textCommonStyle = ({ fontColor, fontSize }: FontProps) => css({
  color: guaranteeHash(fontColor),
  fontSize: `${fontSize}px`,
  paddingLeft: 8,
  textDecoration: 'none',
});

const ellipsisStyle = css({
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

const contactDataWrapperStyle = (fontProps: FontProps) => css(textCommonStyle(fontProps), {
  alignItems: 'center',
  display: 'flex',
});

const basicDataRecordWrapperStyle = (fontProps: FontProps) => css(
  textCommonStyle(fontProps),
  { paddingLeft: 0 }
);

const linkStyle = (fontProps: FontProps, indent: boolean) => css(
  ellipsisStyle,
  textCommonStyle(fontProps), {
    display: 'block',
    paddingLeft: indent ? undefined : 0,
    width: '100%',
  }
);

const iconsStyle = css({
  fontSize: 'calc(100% + 2px)',
  fontWeight: 900,
  lineHeight: '24px',
  marginRight: 8,
});

const dataBodyRecordStyle = ({ verticalGap, ...fontProps }: FontProps & {
  verticalGap: string;
}) => css(textCommonStyle(fontProps), {
  marginTop: `${verticalGap}px`,
});

const labelStyle = css({ textTransform: 'uppercase' });

type BubbleItemProps = Readonly<{
  item: Required<BubbleEditorItem>;
  bulkStyles: ColumnStyleSettingsData;
  isDraggable: boolean;
  openLinksInNewTab?: boolean;
}>;

export const BubbleItemComponent = forwardRef<HTMLDivElement, BubbleItemProps>(({
  item, isDraggable, openLinksInNewTab, bulkStyles,
}, ref) => {
  const [icon] = useState(getRelevantIcon(item.itemType));

  const { labelFontColor, labelFontSize, showLabel, valueFontColor, valueFontSize, verticalGap } = getItemStyles(item, bulkStyles);
  const valueFontProps = { fontColor: valueFontColor, fontSize: valueFontSize };
  const labelFontProps = { fontColor: labelFontColor, fontSize: labelFontSize };

  const handleMouseDownCallback = useCallback(
    (e: MouseEvent<HTMLDivElement>) => {
      if (isDraggable) {
        e.preventDefault();
      }
    },
    [isDraggable],
  );

  const isTextLinkTestResults = useMemo(() => {
    const isWebLink = /^(?:https?:\/\/|www\.)/.test(item.value);
    const isEmailLink = validateEmail(item.value);
    const isPhoneNumber = getIsPhoneNumber(item.value);
    return {
      isWebLink,
      isEmailLink,
      isPhoneNumber,
      isLink: isWebLink || isEmailLink || isPhoneNumber,
    };
  }, [item.value]);

  const isLink = useMemo(() => {
    if (item.itemType && [ColumnRole.Email, ColumnRole.URL, ColumnRole.Image].includes(item.itemType)) {
      return true;
    }

    return isTextLinkTestResults.isLink;
  }, [isTextLinkTestResults.isLink, item.itemType]);

  const hrefLink = useMemo(() => {
    if (item.itemType === ColumnRole.Email || isTextLinkTestResults.isEmailLink) {
      return `mailto:${item.value}`;
    }
    if (isTextLinkTestResults.isWebLink) {
      if (item.value.startsWith('http://') || item.value.startsWith('https://')) {
        return item.value;
      }
      else {
        return `//${item.value}`;
      }
    }
    if (isTextLinkTestResults.isPhoneNumber) {
      return `tel:${item.value}`;
    }
    return item.value;
  }, [isTextLinkTestResults, item.itemType, item.value]);

  const isContactItem = !!icon;

  const wrapperStyle = isContactItem
    ? contactDataWrapperStyle(valueFontProps)
    : basicDataRecordWrapperStyle(labelFontProps);

  const itemStyle = isContactItem || isLink
    ? linkStyle(valueFontProps, !isContactItem)
    : dataBodyRecordStyle({ ...valueFontProps, verticalGap });

  return (
    <div
      css={wrapperStyle}
      ref={ref}
      onMouseDown={handleMouseDownCallback}
      draggable={false}
    >
      {icon && <FontAwesomeIcon icon={icon} css={iconsStyle} />}

      {!isContactItem && showLabel && (
        <div css={labelStyle}>{item.itemData.name}</div>
      )}

      {isLink && !isDraggable ? (
        <a
          href={hrefLink}
          target={openLinksInNewTab ? '_blank' : undefined}
          css={itemStyle}
          rel="noreferrer"
        >
          {item.value}
        </a>
      ) : (
        <LimitNumberOfLinesComponent
          maxNumberOfLines={isDraggable ? 4 : 1000}
          css={itemStyle}
        >
          {item.value}
        </LimitNumberOfLinesComponent>
      )}
    </div>
  );
});

const getRelevantIcon = (type: ColumnRole | null): IconProp | null => {
  switch (type) {
    case ColumnRole.Phone:
      return faPhone;
    case ColumnRole.Email:
      return faPaperPlane;
    case ColumnRole.URL:
      return faWindow;

    default:
      return null;
  }
};
